import React, { useState } from "react";
import axios from "axios";
import { FadeUp } from "../motion/Reveal";
import { ArrowUpRight, MessageCircle, Calendar, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { getUtms } from "../../lib/utm";
import { trackEvent } from "../../lib/track";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SERVICES = ["App Mobile", "Site / Landing", "IA / Chatbot", "Automação", "Não sei ainda"];
const BUDGETS = ["Até R$ 20k", "R$ 20k – 50k", "R$ 50k – 150k", "Acima de R$ 150k"];

const WHATSAPP_URL =
    "https://wa.me/558581220308?text=Ol%C3%A1%20GreenDuck!%20Gostaria%20de%20conversar%20sobre%20um%20projeto.";
const CALENDLY_URL = "https://calendly.com/contatogreenduck/30min";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: SERVICES[0],
        budget: BUDGETS[1],
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    function update(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            await axios.post(`${BACKEND}/api/leads`, { ...form, utm: getUtms() });
            setSent(true);
            toast.success("Recebemos seu contato! Vamos responder em até 24h.");
            // GTM / GA4 conversion event
            if (typeof window !== "undefined") {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "generate_lead",
                    lead_source: "site_form_home",
                    service: form.service,
                    budget: form.budget,
                });
                if (typeof window.gtag === "function") {
                    window.gtag("event", "generate_lead", {
                        method: "site_form_home",
                        service: form.service,
                    });
                }
            }
            setForm({
                name: "",
                email: "",
                phone: "",
                company: "",
                service: SERVICES[0],
                budget: BUDGETS[1],
                message: "",
            });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Não foi possível enviar. Tente novamente.";
            toast.error(typeof msg === "string" ? msg : "Erro ao enviar");
        } finally {
            setLoading(false);
        }
    }

    return (
        <section
            id="contato"
            className="relative py-20 sm:py-24 md:py-40 border-b border-white/10"
            data-testid="contact-section"
        >
            <Toaster
                position="top-center"
                theme="dark"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#fff",
                    },
                }}
            />
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10">
                <div className="grid grid-cols-12 gap-8 md:gap-16">
                    {/* Left column: Big CTA */}
                    <div className="col-span-12 lg:col-span-5">
                        <FadeUp>
                            <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                                (Contato) · Vamos construir?
                            </span>
                            <h2 className="mt-6 font-display uppercase text-4xl sm:text-5xl md:text-7xl leading-[0.95] tracking-tighter text-white">
                                Conte
                                <br />
                                <span className="text-white/40">sobre o</span>
                                <br />
                                <span className="text-[#00FF66]">projeto.</span>
                            </h2>
                            <p className="mt-8 text-white/60 leading-relaxed max-w-md">
                                Preencha o formulário ou fale direto pelo
                                WhatsApp. Respondemos em até 24 horas úteis com
                                próximos passos e escopo preliminar.
                            </p>

                            <div className="mt-10 space-y-4">
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() =>
                                        trackEvent("contact_whatsapp", {
                                            location: "home_contact_section",
                                        })
                                    }
                                    className="group flex items-center justify-between gap-4 border border-white/10 p-4 sm:p-5 hover:border-[#00FF66]/60 hover:bg-[#00FF66]/[0.03] transition-all clip-frame-sm"
                                    data-testid="contact-whatsapp"
                                >
                                    <div className="flex items-center gap-4">
                                        <MessageCircle
                                            className="w-6 h-6 text-[#00FF66]"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <div className="font-display uppercase text-white tracking-tight">
                                                WhatsApp
                                            </div>
                                            <div className="text-white/50 text-sm font-mono-tech">
                                                Resposta em minutos
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-[#00FF66] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                                </a>
                                <a
                                    href={CALENDLY_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() =>
                                        trackEvent("contact_schedule", {
                                            location: "home_contact_section",
                                        })
                                    }
                                    className="group flex items-center justify-between gap-4 border border-white/10 p-4 sm:p-5 hover:border-[#00FF66]/60 hover:bg-[#00FF66]/[0.03] transition-all clip-frame-sm"
                                    data-testid="contact-calendly"
                                >
                                    <div className="flex items-center gap-4">
                                        <Calendar
                                            className="w-6 h-6 text-[#00FF66]"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <div className="font-display uppercase text-white tracking-tight">
                                                Agendar reunião
                                            </div>
                                            <div className="text-white/50 text-sm font-mono-tech">
                                                Escolha um horário · 30 min
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-[#00FF66] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                                </a>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Right column: Form */}
                    <div className="col-span-12 lg:col-span-7">
                        <FadeUp delay={0.1}>
                            {sent ? (
                                <div
                                    className="border border-[#00FF66]/40 bg-[#00FF66]/[0.05] p-6 sm:p-10 md:p-14 clip-frame-sm min-h-[360px] md:min-h-[420px] flex flex-col items-start justify-center"
                                    data-testid="contact-success"
                                >
                                    <CheckCircle2
                                        className="w-14 h-14 text-[#00FF66] mb-6"
                                        strokeWidth={1.4}
                                    />
                                    <h3 className="font-display uppercase text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">
                                        Contato recebido.
                                    </h3>
                                    <p className="mt-4 text-white/70 max-w-md">
                                        Nosso time entrará em contato em até 24
                                        horas úteis. Enquanto isso, você
                                        também pode nos chamar no WhatsApp.
                                    </p>
                                    <button
                                        onClick={() => setSent(false)}
                                        className="mt-8 btn-ghost text-sm"
                                        data-testid="contact-send-another"
                                    >
                                        Enviar outra mensagem
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    className="w-full max-w-full overflow-hidden border border-white/10 bg-[#0a0a0a] p-5 sm:p-8 md:p-10 clip-frame-sm space-y-5 sm:space-y-6"
                                    data-testid="contact-form"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                                        <Field
                                            label="Nome*"
                                            value={form.name}
                                            onChange={(v) =>
                                                update("name", v)
                                            }
                                            required
                                            testid="input-name"
                                        />
                                        <Field
                                            label="E-mail*"
                                            type="email"
                                            value={form.email}
                                            onChange={(v) =>
                                                update("email", v)
                                            }
                                            required
                                            testid="input-email"
                                        />
                                        <Field
                                            label="Telefone / WhatsApp"
                                            value={form.phone}
                                            onChange={(v) =>
                                                update("phone", v)
                                            }
                                            testid="input-phone"
                                        />
                                        <Field
                                            label="Empresa"
                                            value={form.company}
                                            onChange={(v) =>
                                                update("company", v)
                                            }
                                            testid="input-company"
                                        />
                                        <SelectField
                                            label="Serviço"
                                            value={form.service}
                                            onChange={(v) =>
                                                update("service", v)
                                            }
                                            options={SERVICES}
                                            testid="input-service"
                                        />
                                        <SelectField
                                            label="Orçamento estimado"
                                            value={form.budget}
                                            onChange={(v) =>
                                                update("budget", v)
                                            }
                                            options={BUDGETS}
                                            testid="input-budget"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                                            Sobre o projeto*
                                        </label>
                                        <textarea
                                            required
                                            rows={5}
                                            value={form.message}
                                            onChange={(e) =>
                                                update(
                                                    "message",
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="Conte um pouco sobre o problema que você quer resolver..."
                                            className="w-full max-w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-sm sm:text-base text-white placeholder:text-white/30 transition-colors resize-none"
                                            data-testid="input-message"
                                        />
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between pt-2 sm:pt-4">
                                        <p className="text-xs text-white/40 font-mono-tech uppercase tracking-widest">
                                            Resposta em até 24h úteis
                                        </p>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="btn-neon w-full justify-center sm:w-auto sm:justify-start disabled:opacity-60 disabled:cursor-not-allowed"
                                            data-testid="contact-submit"
                                        >
                                            {loading
                                                ? "Enviando..."
                                                : "Enviar mensagem"}
                                            <ArrowUpRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </FadeUp>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
    return (
        <div className="min-w-0">
            <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="w-full max-w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-sm sm:text-base text-white placeholder:text-white/30 transition-colors"
                data-testid={testid}
            />
        </div>
    );
}

function SelectField({ label, value, onChange, options, testid }) {
    return (
        <div className="min-w-0">
            <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                {label}
            </label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full max-w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-sm sm:text-base text-white transition-colors"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o} value={o} className="bg-[#0a0a0a]">
                        {o}
                    </option>
                ))}
            </select>
        </div>
    );
}
